import type { FacetedFilterOption } from '~/components/ui/FacetedFilterSelect';
import type { Attribute } from '~/gql/graphql';
import type { IAttribute } from '~/types';
import { unique } from './array';

type Contact = {
  id: string;
  name: string;
};

/**
 * Resolves contact IDs to names for contact-type attributes
 * Contact values are stored as JSON strings that need to be parsed
 */
export function resolveContactValues(
  attribute: { type?: string; value: string[] },
  contacts: readonly Contact[] | undefined
): string[] {
  if (attribute.type === 'contact' && contacts) {
    return attribute.value.flatMap((jsonValue) => {
      // Contact values are stored as JSON strings like '["19","20"]'
      let contactIds: string[];
      try {
        const parsed = JSON.parse(jsonValue);
        contactIds = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // If parsing fails, treat as a single ID
        contactIds = [jsonValue];
      }

      return contactIds
        .map((contactId) => {
          const contact = contacts.find((c) => c.id === contactId);
          return contact ? contact.name : contactId; // fallback to ID if contact not found
        })
        .filter(Boolean);
    });
  }
  return attribute.value;
}

export function extractAttributes(
  attributes: Record<string, string> | IAttribute[] | undefined
) {
  if (attributes) {
    return Array.isArray(attributes)
      ? attributes.flatMap(({ name, value }) =>
          value.map((s) => `${name}: ${s}`)
        )
      : Object.keys(attributes).map((key) => `${key}: ${attributes[key]}`);
  }
  return [];
}

export type ContainsAttributes = {
  // Accept raw GraphQL Attribute arrays directly (preferred)
  attributes?: Attribute[] | null;
};

export function extractAttributeFilterOptions(
  data: ContainsAttributes[],
  contacts?: readonly Contact[]
) {
  return (
    data?.reduce<FacetedFilterOption[]>((acc, item) => {
      const attributes = item?.attributes ?? [];

      attributes?.forEach((attribute) => {
        if (attribute.type === 'attachment') {
          return;
        }

        const index = acc.findIndex(
          (opt: FacetedFilterOption) => opt.key === attribute.id
        );
        const resolvedValue = resolveContactValues(attribute, contacts);

        if (index === -1) {
          const group = attribute.category;
          const label = attribute.name;
          acc.push({
            $group: group?.trimEnd(),
            key: attribute.id,
            label: label.trimStart(),
            value: resolvedValue,
            type: attribute.type,
          });
        } else {
          acc[index].value = unique([...acc[index].value, ...resolvedValue]);
        }
      });

      return acc;
    }, []) ?? []
  );
}
